<script lang="js">
import Swal from "sweetalert2";
import axios from "axios";

export default {
  name: "Careers",
  data() {
    return {
      logger: null,
      // Tab management
      activeTab: "jobs",

      // Jobs data
      jobs: [],
      jobsLoading: false,
      jobSearchQuery: "",
      jobCurrentPage: 1,
      jobPerPage: 10,
      jobTotalPages: 1,
      jobTotal: 0,
      jobFilterActive: "",



      // Applications data
      applications: [],
      applicationsLoading: false,
      appSearchQuery: "",
      appCurrentPage: 1,
      appPerPage: 10,
      appTotalPages: 1,
      appTotal: 0,
      appFilterStatus: "",
      appFilterJob: "",

      // Selected application for detail view
      selectedApplication: null,

      // Enums
      employmentTypes: ["Full-Time", "Part-Time", "Contract", "Temporary", "Internship"],
      locations: ["On-Site", "Remote", "Hybrid"],
      applicationStatuses: ["pending", "reviewed", "shortlisted", "rejected", "accepted"],
    };
  },
  created() {
    this.logger = this.$createLogger("CareersMgt");
  },
  mounted() {
    this.fetchJobs();
    this.fetchApplications();
  },
  methods: {
    // ─── Tab Management ──────────────────────────────
    switchTab(tab) {
      this.activeTab = tab;
    },

    // ─── Jobs CRUD ───────────────────────────────────
    async fetchJobs() {
      try {
        this.jobsLoading = true;
        const params = {
          page: this.jobCurrentPage,
          limit: this.jobPerPage,
          sortBy: "createdAt",
          sortOrder: "desc",
        };
        if (this.jobSearchQuery) params.search = this.jobSearchQuery;
        if (this.jobFilterActive !== "") params.active = this.jobFilterActive;

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/jobs`,
          { params }
        );

        if (response.data.success) {
          this.jobs = response.data.data.jobs;
          const pagination = response.data.data.pagination;
          this.jobTotalPages = pagination.totalPages;
          this.jobTotal = pagination.total;
        }
      } catch (error) {
        this.logger.error("Error fetching jobs:", error);
        Swal.fire("Error", "Failed to fetch job postings", "error");
      } finally {
        this.jobsLoading = false;
      }
    },

    openCreateJobForm() {
      this.showJobModal(null);
    },

    openEditJobForm(job) {
      this.showJobModal(job);
    },

    _buildDynamicListHtml(id, label, items) {
      let rows = items
        .map(
          (item, i) =>
            `<div class="input-group input-group-sm mb-2" data-list="${id}">
              <input type="text" class="form-control" value="${item.replace(/"/g, "&quot;")}" placeholder="Add ${label.toLowerCase()}" />
              <button type="button" class="btn btn-outline-danger" data-remove="${id}" data-index="${i}" ${items.length <= 1 ? "disabled" : ""}>
                <i class="bi bi-x"></i>
              </button>
            </div>`
        )
        .join("");
      return `<div id="${id}-container">${rows}</div>
        <button type="button" class="btn btn-outline-secondary btn-sm mt-1" data-add="${id}">
          <i class="bi bi-plus me-1"></i> Add
        </button>`;
    },

    _attachDynamicListListeners(popup, id, label) {
      const container = popup.querySelector(`#${id}-container`);
      const addBtn = popup.querySelector(`[data-add="${id}"]`);

      const refreshRemoveState = () => {
        const groups = container.querySelectorAll(`[data-list="${id}"]`);
        groups.forEach((g, i) => {
          const btn = g.querySelector(`[data-remove="${id}"]`);
          btn.disabled = groups.length <= 1;
          btn.dataset.index = i;
        });
      };

      addBtn.addEventListener("click", () => {
        const row = document.createElement("div");
        row.className = "input-group input-group-sm mb-2";
        row.dataset.list = id;
        row.innerHTML = `<input type="text" class="form-control" placeholder="Add ${label.toLowerCase()}" />
          <button type="button" class="btn btn-outline-danger" data-remove="${id}" data-index="0">
            <i class="bi bi-x"></i>
          </button>`;
        container.appendChild(row);
        refreshRemoveState();
        row.querySelector("input").focus();
      });

      container.addEventListener("click", (e) => {
        const removeBtn = e.target.closest(`[data-remove="${id}"]`);
        if (removeBtn) {
          removeBtn.closest(`[data-list="${id}"]`).remove();
          refreshRemoveState();
        }
      });
    },

    _getListValues(popup, id) {
      return Array.from(
        popup.querySelectorAll(`[data-list="${id}"] input`)
      )
        .map((input) => input.value.trim())
        .filter(Boolean);
    },

    async showJobModal(job) {
      const isEdit = !!job;
      const form = isEdit
        ? {
          title: job.title,
          shortDescription: job.shortDescription,
          description: job.description,
          responsibilities:
            job.responsibilities?.length > 0 ? [...job.responsibilities] : [""],
          requirements:
            job.requirements?.length > 0 ? [...job.requirements] : [""],
          employmentType: job.employmentType,
          location: job.location,
          department: job.department || "",
          tags: job.tags ? job.tags.join(", ") : "",
        }
        : {
          title: "",
          shortDescription: "",
          description: "",
          responsibilities: [""],
          requirements: [""],
          employmentType: "",
          location: "On-Site",
          department: "",
          tags: "",
        };

      const typeOptions = this.employmentTypes
        .map(
          (t) =>
            `<option value="${t}" ${form.employmentType === t ? "selected" : ""}>${t}</option>`
        )
        .join("");
      const locOptions = this.locations
        .map(
          (l) =>
            `<option value="${l}" ${form.location === l ? "selected" : ""}>${l}</option>`
        )
        .join("");

      const htmlContent = `
        <div class="text-start">
          <div class="row">
            <div class="col-md-8 mb-3">
              <label class="form-label fw-semibold">Job Title *</label>
              <input type="text" id="swal-title" class="form-control" value="${form.title.replace(/"/g, "&quot;")}" placeholder="e.g. Lecturer - Computer Science" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">Employment Type *</label>
              <select id="swal-employmentType" class="form-select">
                <option value="" disabled ${!form.employmentType ? "selected" : ""}>Select type</option>
                ${typeOptions}
              </select>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Short Description * <small class="text-muted fw-normal">(max 200 chars)</small></label>
            <input type="text" id="swal-shortDescription" class="form-control" maxlength="200" value="${form.shortDescription.replace(/"/g, "&quot;")}" placeholder="Brief summary of the role" />
            <small class="text-muted" id="swal-charCount">${form.shortDescription.length}/200</small>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Full Description *</label>
            <textarea id="swal-description" class="form-control" rows="4" placeholder="Detailed description of the role...">${form.description}</textarea>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">Responsibilities</label>
              ${this._buildDynamicListHtml("resp", "Responsibility", form.responsibilities)}
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label fw-semibold">Requirements</label>
              ${this._buildDynamicListHtml("req", "Requirement", form.requirements)}
            </div>
          </div>

          <div class="row">
            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">Location</label>
              <select id="swal-location" class="form-select">
                ${locOptions}
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">Department</label>
              <input type="text" id="swal-department" class="form-control" value="${form.department.replace(/"/g, "&quot;")}" placeholder="e.g. Academic Affairs" />
            </div>
            <div class="col-md-4 mb-3">
              <label class="form-label fw-semibold">Tags <small class="text-muted fw-normal">(comma-separated)</small></label>
              <input type="text" id="swal-tags" class="form-control" value="${form.tags.replace(/"/g, "&quot;")}" placeholder="e.g. Teaching, Research, IT" />
            </div>
          </div>
        </div>`;

      const result = await Swal.fire({
        title: isEdit ? "Edit Job Posting" : "Create New Job Posting",
        html: htmlContent,
        width: "800px",
        showCancelButton: true,
        confirmButtonText: isEdit ? "Update Job" : "Create Job",
        cancelButtonText: "Cancel",
        customClass: {
          popup: "swal-job-modal",
          htmlContainer: "swal-job-scroll",
          confirmButton: "btn btn-primary me-2",
          cancelButton: "btn btn-outline-secondary",
        },
        buttonsStyling: false,
        didOpen: (popup) => {
          // Char counter for short description
          const shortDescInput = popup.querySelector("#swal-shortDescription");
          const charCount = popup.querySelector("#swal-charCount");
          shortDescInput.addEventListener("input", () => {
            charCount.textContent = `${shortDescInput.value.length}/200`;
          });

          // Dynamic list listeners
          this._attachDynamicListListeners(popup, "resp", "Responsibility");
          this._attachDynamicListListeners(popup, "req", "Requirement");
        },
        preConfirm: () => {
          const popup = Swal.getPopup();
          const title = popup.querySelector("#swal-title").value.trim();
          const shortDescription = popup.querySelector("#swal-shortDescription").value.trim();
          const description = popup.querySelector("#swal-description").value.trim();
          const employmentType = popup.querySelector("#swal-employmentType").value;

          if (!title || !shortDescription || !description || !employmentType) {
            Swal.showValidationMessage("Please fill in all required fields.");
            return false;
          }

          return {
            title,
            shortDescription,
            description,
            employmentType,
            location: popup.querySelector("#swal-location").value,
            department: popup.querySelector("#swal-department").value.trim(),
            responsibilities: this._getListValues(popup, "resp"),
            requirements: this._getListValues(popup, "req"),
            tags: popup
              .querySelector("#swal-tags")
              .value.split(",")
              .map((t) => t.trim())
              .filter(Boolean),
          };
        },
      });

      if (!result.isConfirmed || !result.value) return;

      try {
        Swal.fire({
          title: isEdit ? "Updating..." : "Creating...",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        let response;
        if (isEdit) {
          response = await axios.put(
            `${process.env.VUE_APP_API_BASE_URL}/jobs/${job._id}`,
            result.value
          );
        } else {
          response = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/jobs`,
            result.value
          );
        }

        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: isEdit ? "Job Updated" : "Job Created",
            text: response.data.message,
            showConfirmButton: false,
            timer: 1500,
          });
          this.fetchJobs();
        }
      } catch (error) {
        this.logger.error("Error saving job:", error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "Failed to save job posting",
          "error"
        );
      }
    },

    async toggleJobActive(job) {
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/jobs/${job._id}/toggle-active`
        );
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: response.data.data.active ? "Activated" : "Deactivated",
            text: response.data.message,
            showConfirmButton: false,
            timer: 1500,
          });
          this.fetchJobs();
        }
      } catch (error) {
        this.logger.error("Error toggling job status:", error);
        Swal.fire("Error", "Failed to update job status", "error");
      }
    },

    async deleteJob(job) {
      const result = await Swal.fire({
        title: "Delete Job Posting?",
        text: `Are you sure you want to delete "${job.title}"? This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#888",
        confirmButtonText: "Yes, delete it",
      });

      if (result.isConfirmed) {
        try {
          const response = await axios.delete(
            `${process.env.VUE_APP_API_BASE_URL}/jobs/${job._id}`
          );
          if (response.data.success) {
            Swal.fire({
              icon: "success",
              title: "Deleted",
              text: response.data.message,
              showConfirmButton: false,
              timer: 1500,
            });
            this.fetchJobs();
          }
        } catch (error) {
          this.logger.error("Error deleting job:", error);
          Swal.fire(
            "Error",
            error.response?.data?.message || "Failed to delete job",
            "error"
          );
        }
      }
    },

    // Job pagination
    jobPreviousPage() {
      if (this.jobCurrentPage > 1) {
        this.jobCurrentPage--;
        this.fetchJobs();
      }
    },
    jobNextPage() {
      if (this.jobCurrentPage < this.jobTotalPages) {
        this.jobCurrentPage++;
        this.fetchJobs();
      }
    },
    jobGoToPage(page) {
      this.jobCurrentPage = page;
      this.fetchJobs();
    },

    searchJobs() {
      this.jobCurrentPage = 1;
      this.fetchJobs();
    },

    filterJobs() {
      this.jobCurrentPage = 1;
      this.fetchJobs();
    },

    // ─── Applications Management ─────────────────────
    async fetchApplications() {
      try {
        this.applicationsLoading = true;
        const params = {
          page: this.appCurrentPage,
          limit: this.appPerPage,
          sortBy: "createdAt",
          sortOrder: "desc",
        };
        if (this.appSearchQuery) params.search = this.appSearchQuery;
        if (this.appFilterStatus) params.status = this.appFilterStatus;
        if (this.appFilterJob) params.jobId = this.appFilterJob;

        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/job-applications`,
          { params }
        );

        if (response.data.success) {
          this.applications = response.data.data.applications;
          const pagination = response.data.data.pagination;
          this.appTotalPages = pagination.totalPages;
          this.appTotal = pagination.total;
        }
      } catch (error) {
        this.logger.error("Error fetching applications:", error);
        Swal.fire("Error", "Failed to fetch applications", "error");
      } finally {
        this.applicationsLoading = false;
      }
    },

    viewApplication(application) {
      this.selectedApplication = application;
    },

    clearSelectedApplication() {
      this.selectedApplication = null;
    },

    async updateApplicationStatus(application, status) {
      try {
        const response = await axios.put(
          `${process.env.VUE_APP_API_BASE_URL}/job-applications/${application.applicationId}/status`,
          { status }
        );
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Status Updated",
            text: `Application status changed to ${status}`,
            showConfirmButton: false,
            timer: 1500,
          });
          this.fetchApplications();
          if (this.selectedApplication && this.selectedApplication._id === application._id) {
            this.selectedApplication.status = status;
          }
        }
      } catch (error) {
        this.logger.error("Error updating application status:", error);
        Swal.fire("Error", "Failed to update application status", "error");
      }
    },

    async deleteApplication(application) {
      const result = await Swal.fire({
        title: "Delete Application?",
        text: `Delete application ${application.applicationId}? This will also remove uploaded files.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#888",
        confirmButtonText: "Yes, delete it",
      });

      if (result.isConfirmed) {
        try {
          const response = await axios.delete(
            `${process.env.VUE_APP_API_BASE_URL}/job-applications/${application.applicationId}`
          );
          if (response.data.success) {
            Swal.fire({
              icon: "success",
              title: "Deleted",
              text: response.data.message,
              showConfirmButton: false,
              timer: 1500,
            });
            this.fetchApplications();
            if (this.selectedApplication && this.selectedApplication._id === application._id) {
              this.selectedApplication = null;
            }
          }
        } catch (error) {
          this.logger.error("Error deleting application:", error);
          Swal.fire("Error", "Failed to delete application", "error");
        }
      }
    },

    // Application pagination
    appPreviousPage() {
      if (this.appCurrentPage > 1) {
        this.appCurrentPage--;
        this.fetchApplications();
      }
    },
    appNextPage() {
      if (this.appCurrentPage < this.appTotalPages) {
        this.appCurrentPage++;
        this.fetchApplications();
      }
    },
    appGoToPage(page) {
      this.appCurrentPage = page;
      this.fetchApplications();
    },

    searchApplications() {
      this.appCurrentPage = 1;
      this.fetchApplications();
    },

    filterApplications() {
      this.appCurrentPage = 1;
      this.fetchApplications();
    },

    // ─── Utility Methods ─────────────────────────────
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-NG", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getStatusBadgeClass(status) {
      const classes = {
        pending: "bg-warning text-dark",
        reviewed: "bg-info text-white",
        shortlisted: "bg-primary",
        rejected: "bg-danger",
        accepted: "bg-success",
      };
      return classes[status] || "bg-secondary";
    },

    getTypeColor(type) {
      const colors = {
        "Full-Time": "primary",
        "Part-Time": "info",
        Contract: "warning",
        Temporary: "secondary",
        Internship: "success",
      };
      return colors[type] || "secondary";
    },

    openFileInNewTab(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },
  },

  computed: {
    jobStartItem() {
      if (this.jobTotal === 0) return 0;
      return (this.jobCurrentPage - 1) * this.jobPerPage + 1;
    },
    jobEndItem() {
      let end = this.jobCurrentPage * this.jobPerPage;
      return end > this.jobTotal ? this.jobTotal : end;
    },
    jobPaginationPages() {
      let pages = [];
      const total = this.jobTotalPages;
      if (total <= 3) {
        pages = Array.from({ length: total }, (_, i) => i + 1);
      } else if (this.jobCurrentPage <= 3) {
        pages = [1, 2, 3, "...", total];
      } else if (this.jobCurrentPage > 3 && this.jobCurrentPage < total - 1) {
        pages = [1, "...", this.jobCurrentPage, "...", total];
      } else {
        pages = [1, "...", total - 2, total - 1, total];
      }
      return pages;
    },
    appStartItem() {
      if (this.appTotal === 0) return 0;
      return (this.appCurrentPage - 1) * this.appPerPage + 1;
    },
    appEndItem() {
      let end = this.appCurrentPage * this.appPerPage;
      return end > this.appTotal ? this.appTotal : end;
    },
    appPaginationPages() {
      let pages = [];
      const total = this.appTotalPages;
      if (total <= 3) {
        pages = Array.from({ length: total }, (_, i) => i + 1);
      } else if (this.appCurrentPage <= 3) {
        pages = [1, 2, 3, "...", total];
      } else if (this.appCurrentPage > 3 && this.appCurrentPage < total - 1) {
        pages = [1, "...", this.appCurrentPage, "...", total];
      } else {
        pages = [1, "...", total - 2, total - 1, total];
      }
      return pages;
    },
    // Unique job IDs for the application filter dropdown
    uniqueJobIds() {
      const jobMap = {};
      this.applications.forEach((app) => {
        if (app.job) {
          jobMap[app.job.jobId] = app.job.title;
        }
      });
      return Object.entries(jobMap).map(([id, title]) => ({ id, title }));
    },
  },
};
</script>

<template>
  <div class="col-lg-12 col-md-12">
    <div class="p-4 text-start">
      <h4 class="fw-bold">Careers</h4>
      <p class="text-muted">Manage job postings and review applications</p>
      <hr class="mb-4" />

      <!-- Tabs -->
      <ul class="nav nav-tabs mb-4">
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'jobs' }"
            href="#"
            @click.prevent="switchTab('jobs')"
          >
            <i class="bi bi-briefcase me-1"></i> Job Postings
            <span class="badge bg-secondary ms-1">{{ jobTotal }}</span>
          </a>
        </li>
        <li class="nav-item">
          <a
            class="nav-link"
            :class="{ active: activeTab === 'applications' }"
            href="#"
            @click.prevent="switchTab('applications')"
          >
            <i class="bi bi-file-earmark-person me-1"></i> Applications
            <span class="badge bg-secondary ms-1">{{ appTotal }}</span>
          </a>
        </li>
      </ul>

      <!-- ═══════════════ JOBS TAB ═══════════════ -->
      <div v-show="activeTab === 'jobs'">
        <!-- Jobs Toolbar -->
        <div class="row mb-3">
          <div class="col-md-4 d-flex align-items-center gap-2">
            <button class="btn btn-primary btn-sm" @click="openCreateJobForm">
              <i class="bi bi-plus-circle me-1"></i> New Job
            </button>
            <select
              class="form-select form-select-sm"
              style="max-width: 150px"
              v-model="jobFilterActive"
              @change="filterJobs"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div class="col-md-8">
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-white">
                <i class="bi bi-search"></i>
              </span>
              <input
                v-model="jobSearchQuery"
                class="form-control border-start-0"
                type="text"
                placeholder="Search by title, department, or ID..."
                @keyup.enter="searchJobs"
              />
              <button class="btn btn-outline-secondary" @click="searchJobs">
                Search
              </button>
            </div>
          </div>
        </div>

        <!-- Jobs Table -->
        <div class="table-responsive small">
          <div v-if="jobsLoading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading...</span>
          </div>

          <table v-else class="table table-hover table-sm">
            <caption>
              Showing
              {{
                jobStartItem
              }}
              to
              {{
                jobEndItem
              }}
              of
              {{
                jobTotal
              }}
              entries
            </caption>
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Title</th>
                <th>Type</th>
                <th>Department</th>
                <th>Applications</th>
                <th>Status</th>
                <th>Posted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="jobs.length === 0">
                <td colspan="8" class="text-center text-muted py-4">
                  No job postings found
                </td>
              </tr>
              <tr v-for="job in jobs" :key="job._id">
                <td class="text-nowrap">
                  <code>{{ job.jobId }}</code>
                </td>
                <td>{{ job.title }}</td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="`bg-${getTypeColor(job.employmentType)}`"
                  >
                    {{ job.employmentType }}
                  </span>
                </td>
                <td>{{ job.department || "—" }}</td>
                <td>
                  <span class="badge bg-secondary">{{
                    job.applicationCount || 0
                  }}</span>
                </td>
                <td>
                  <span
                    class="badge"
                    :class="job.active ? 'bg-success' : 'bg-danger'"
                  >
                    {{ job.active ? "Active" : "Inactive" }}
                  </span>
                </td>
                <td class="text-nowrap">{{ formatDate(job.createdAt) }}</td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button
                      class="btn btn-outline-primary"
                      title="Edit"
                      @click="openEditJobForm(job)"
                    >
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button
                      class="btn btn-outline-secondary"
                      :title="job.active ? 'Deactivate' : 'Activate'"
                      @click="toggleJobActive(job)"
                    >
                      <i
                        class="bi"
                        :class="job.active ? 'bi-toggle-on' : 'bi-toggle-off'"
                      ></i>
                    </button>
                    <button
                      class="btn btn-outline-danger"
                      title="Delete"
                      @click="deleteJob(job)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Jobs Pagination -->
          <nav v-if="jobTotalPages > 1" aria-label="Jobs pagination">
            <ul class="pagination pagination-sm justify-content-center">
              <li class="page-item" :class="{ disabled: jobCurrentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="jobPreviousPage"
                  >Previous</a
                >
              </li>
              <li
                v-for="page in jobPaginationPages"
                :key="'jp-' + page"
                class="page-item"
                :class="{
                  active: page === jobCurrentPage,
                  disabled: page === '...',
                }"
              >
                <a
                  class="page-link"
                  href="#"
                  @click.prevent="page !== '...' && jobGoToPage(page)"
                >
                  {{ page }}
                </a>
              </li>
              <li
                class="page-item"
                :class="{ disabled: jobCurrentPage === jobTotalPages }"
              >
                <a class="page-link" href="#" @click.prevent="jobNextPage"
                  >Next</a
                >
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- ═══════════════ APPLICATIONS TAB ═══════════════ -->
      <div v-show="activeTab === 'applications'">
        <!-- Applications Toolbar -->
        <div class="row mb-3">
          <div class="col-md-5 d-flex align-items-center gap-2">
            <select
              class="form-select form-select-sm"
              style="max-width: 140px"
              v-model="appFilterStatus"
              @change="filterApplications"
            >
              <option value="">All Status</option>
              <option
                v-for="status in applicationStatuses"
                :key="status"
                :value="status"
              >
                {{ status.charAt(0).toUpperCase() + status.slice(1) }}
              </option>
            </select>
            <select
              class="form-select form-select-sm"
              style="max-width: 200px"
              v-model="appFilterJob"
              @change="filterApplications"
            >
              <option value="">All Jobs</option>
              <option v-for="j in uniqueJobIds" :key="j.id" :value="j.id">
                {{ j.title }}
              </option>
            </select>
          </div>
          <div class="col-md-7">
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-white">
                <i class="bi bi-search"></i>
              </span>
              <input
                v-model="appSearchQuery"
                class="form-control border-start-0"
                type="text"
                placeholder="Search by name, email, or application ID..."
                @keyup.enter="searchApplications"
              />
              <button
                class="btn btn-outline-secondary"
                @click="searchApplications"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        <!-- Applications Table -->
        <div class="table-responsive small">
          <div v-if="applicationsLoading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2">Loading...</span>
          </div>

          <table v-else class="table table-hover table-sm">
            <caption>
              Showing
              {{
                appStartItem
              }}
              to
              {{
                appEndItem
              }}
              of
              {{
                appTotal
              }}
              entries
            </caption>
            <thead>
              <tr>
                <th>App ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Position</th>
                <th>Experience</th>
                <th>Status</th>
                <th>Applied</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="applications.length === 0">
                <td colspan="8" class="text-center text-muted py-4">
                  No applications found
                </td>
              </tr>
              <tr v-for="app in applications" :key="app._id">
                <td class="text-nowrap">
                  <code>{{ app.applicationId }}</code>
                </td>
                <td class="text-nowrap">
                  {{ app.firstName }} {{ app.lastName }}
                </td>
                <td>{{ app.email }}</td>
                <td>{{ app.job ? app.job.title : "N/A" }}</td>
                <td>{{ app.experienceLevel }}</td>
                <td>
                  <span
                    class="badge rounded-pill"
                    :class="getStatusBadgeClass(app.status)"
                  >
                    {{ app.status }}
                  </span>
                </td>
                <td class="text-nowrap">{{ formatDate(app.createdAt) }}</td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button
                      class="btn btn-outline-primary"
                      title="View Details"
                      data-bs-toggle="modal"
                      data-bs-target="#applicationModal"
                      @click="viewApplication(app)"
                    >
                      <i class="bi bi-eye"></i>
                    </button>
                    <button
                      class="btn btn-outline-danger"
                      title="Delete"
                      @click="deleteApplication(app)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Applications Pagination -->
          <nav v-if="appTotalPages > 1" aria-label="Applications pagination">
            <ul class="pagination pagination-sm justify-content-center">
              <li class="page-item" :class="{ disabled: appCurrentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="appPreviousPage"
                  >Previous</a
                >
              </li>
              <li
                v-for="page in appPaginationPages"
                :key="'ap-' + page"
                class="page-item"
                :class="{
                  active: page === appCurrentPage,
                  disabled: page === '...',
                }"
              >
                <a
                  class="page-link"
                  href="#"
                  @click.prevent="page !== '...' && appGoToPage(page)"
                >
                  {{ page }}
                </a>
              </li>
              <li
                class="page-item"
                :class="{ disabled: appCurrentPage === appTotalPages }"
              >
                <a class="page-link" href="#" @click.prevent="appNextPage"
                  >Next</a
                >
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- ═══════════════ APPLICATION DETAIL MODAL ═══════════════ -->
      <div
        class="modal fade"
        id="applicationModal"
        tabindex="-1"
        aria-labelledby="applicationModalLabel"
        aria-hidden="true"
      >
        <div
          class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        >
          <div class="modal-content" v-if="selectedApplication">
            <div class="modal-header">
              <h5 class="modal-title" id="applicationModalLabel">
                Application: {{ selectedApplication.applicationId }}
              </h5>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                @click="clearSelectedApplication"
              ></button>
            </div>
            <div class="modal-body">
              <!-- Applicant Info -->
              <div class="row mb-4">
                <div class="col-md-6">
                  <h6 class="fw-bold text-muted mb-3">
                    <i class="bi bi-person me-1"></i> Applicant Information
                  </h6>
                  <table class="table table-sm table-borderless">
                    <tr>
                      <td class="text-muted" style="width: 40%">Name</td>
                      <td class="fw-semibold">
                        {{ selectedApplication.firstName }}
                        {{ selectedApplication.lastName }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Email</td>
                      <td>
                        <a :href="'mailto:' + selectedApplication.email">
                          {{ selectedApplication.email }}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Phone</td>
                      <td>
                        <a :href="'tel:' + selectedApplication.phone">
                          {{ selectedApplication.phone }}
                        </a>
                      </td>
                    </tr>
                    <tr v-if="selectedApplication.city">
                      <td class="text-muted">City</td>
                      <td>{{ selectedApplication.city }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Experience</td>
                      <td>{{ selectedApplication.experienceLevel }}</td>
                    </tr>
                    <tr v-if="selectedApplication.preferredStartDate">
                      <td class="text-muted">Preferred Start</td>
                      <td>
                        {{ formatDate(selectedApplication.preferredStartDate) }}
                      </td>
                    </tr>
                  </table>
                </div>
                <div class="col-md-6">
                  <h6 class="fw-bold text-muted mb-3">
                    <i class="bi bi-briefcase me-1"></i> Job Information
                  </h6>
                  <table class="table table-sm table-borderless">
                    <tr>
                      <td class="text-muted" style="width: 40%">Position</td>
                      <td class="fw-semibold">
                        {{ selectedApplication.job?.title || "N/A" }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Job ID</td>
                      <td>
                        <code>{{
                          selectedApplication.job?.jobId || "N/A"
                        }}</code>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Department</td>
                      <td>
                        {{ selectedApplication.job?.department || "N/A" }}
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Applied On</td>
                      <td>{{ formatDate(selectedApplication.createdAt) }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Status</td>
                      <td>
                        <span
                          class="badge rounded-pill"
                          :class="
                            getStatusBadgeClass(selectedApplication.status)
                          "
                        >
                          {{ selectedApplication.status }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              <!-- Message -->
              <div v-if="selectedApplication.message" class="mb-4">
                <h6 class="fw-bold text-muted mb-2">
                  <i class="bi bi-chat-left-text me-1"></i> Message
                </h6>
                <p class="bg-light p-3 rounded" style="white-space: pre-line">
                  {{ selectedApplication.message }}
                </p>
              </div>

              <!-- Documents -->
              <div class="mb-4">
                <h6 class="fw-bold text-muted mb-2">
                  <i class="bi bi-file-earmark me-1"></i> Documents
                </h6>
                <div class="d-flex gap-2">
                  <button
                    v-if="selectedApplication.resumeUrl"
                    class="btn btn-outline-primary btn-sm"
                    @click="openFileInNewTab(selectedApplication.resumeUrl)"
                  >
                    <i class="bi bi-file-pdf me-1"></i> Resume
                  </button>
                  <button
                    v-if="selectedApplication.coverLetterUrl"
                    class="btn btn-outline-primary btn-sm"
                    @click="
                      openFileInNewTab(selectedApplication.coverLetterUrl)
                    "
                  >
                    <i class="bi bi-file-pdf me-1"></i> Cover Letter
                  </button>
                  <span
                    v-if="
                      !selectedApplication.resumeUrl &&
                      !selectedApplication.coverLetterUrl
                    "
                    class="text-muted"
                  >
                    No documents uploaded
                  </span>
                </div>
              </div>

              <!-- Status Actions -->
              <div>
                <h6 class="fw-bold text-muted mb-2">
                  <i class="bi bi-arrow-repeat me-1"></i> Update Status
                </h6>
                <div class="d-flex flex-wrap gap-2">
                  <button
                    v-for="status in applicationStatuses"
                    :key="status"
                    class="btn btn-sm"
                    :class="
                      selectedApplication.status === status
                        ? getStatusBadgeClass(status) + ' text-white'
                        : 'btn-outline-secondary'
                    "
                    :disabled="selectedApplication.status === status"
                    @click="
                      updateApplicationStatus(selectedApplication, status)
                    "
                  >
                    {{ status.charAt(0).toUpperCase() + status.slice(1) }}
                  </button>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                data-bs-dismiss="modal"
                @click="clearSelectedApplication"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
code {
  font-size: 0.85em;
}
</style>

<style>
.swal-job-modal {
  max-height: 85vh !important;
  display: flex !important;
  flex-direction: column !important;
}

.swal-job-scroll {
  overflow-y: auto !important;
  max-height: 60vh !important;
}
</style>
